import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Plus, Loader2, DollarSign, Eye, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const TYPES = ["identity", "employment", "education", "reference", "employer"];
const STATUSES = ["pending", "running", "completed", "failed", "expired"];

const statusTone = (s) => ({
    pending: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
    running: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    completed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    failed: "bg-destructive/10 text-destructive border-destructive/30",
    expired: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
}[s]);

const emptyForm = {
    candidate_email: "",
    candidate_name: "",
    type: "identity",
    provider: "",
    cost: "0",
    pii: { ssn: "", dob: "", doc_type: "", doc_number: "", sensitive_notes: "" },
};

export default function VerificationCenter() {
    const [data, setData] = useState({ results: [], counts: {}, total: 0, total_cost: 0 });
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("any");
    const [filterType, setFilterType] = useState("any");
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    // Reveal dialog state
    const [revealTarget, setRevealTarget] = useState(null); // full row
    const [revealReason, setRevealReason] = useState("");
    const [revealed, setRevealed] = useState(null); // decrypted pii object
    const [revealLoading, setRevealLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterStatus !== "any") params.status = filterStatus;
            if (filterType !== "any") params.type = filterType;
            const { data } = await api.get("/admin/verifications", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterType]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        try {
            setSubmitting(true);
            const pii = { ...form.pii };
            // Only send PII when at least one field is filled — keep payloads minimal.
            const hasPii = Object.values(pii).some((v) => (v || "").toString().trim().length > 0);
            await api.post("/admin/verifications", {
                candidate_email: form.candidate_email,
                candidate_name: form.candidate_name,
                type: form.type,
                provider: form.provider,
                cost: parseFloat(form.cost) || 0,
                ...(hasPii ? { pii } : {}),
            });
            toast.success("Verification request created.");
            setCreateOpen(false);
            setForm(emptyForm);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const updateStatus = async (v, status) => {
        try {
            await api.patch(`/admin/verifications/${v.id}`, { status });
            toast.success(`Marked ${status}.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openReveal = (v) => {
        setRevealTarget(v);
        setRevealReason("");
        setRevealed(null);
    };

    const doReveal = async () => {
        if (!revealTarget) return;
        if ((revealReason || "").trim().length < 8) {
            toast.error("Please provide a justification of at least 8 characters.");
            return;
        }
        try {
            setRevealLoading(true);
            const { data } = await api.post(`/admin/verifications/${revealTarget.id}/reveal`, {
                reason: revealReason.trim(),
            });
            setRevealed(data.pii || {});
            toast.success("PII revealed — access has been logged.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRevealLoading(false);
        }
    };

    return (
        <div data-testid="admin-verification-center">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center · Simonara Verify™</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Verification Center
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Identity, employment, education &amp; reference verifications — powered by Simonara Verify™.</p>
                </div>
                <button onClick={() => setCreateOpen(true)} className="btn-green text-sm inline-flex items-center gap-1" data-testid="ver-new">
                    <Plus size={13} /> New verification
                </button>
            </div>

            <div className="card-soft p-3 mb-4 flex items-start gap-2 bg-emerald-500/5 border-emerald-500/20">
                <Lock size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                    <b className="text-foreground">SOC 2 controls active.</b> Sensitive PII (SSN, DOB, document numbers) is
                    encrypted at rest with AES/Fernet, only masked previews are shown in this list, and every read is
                    written to the immutable audit log. Revealing plaintext requires <b>platform_admin</b> role and a
                    written justification.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-foreground">{data.total}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Running</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-sky-600">{data.counts.running || 0}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Completed</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-emerald-600">{data.counts.completed || 0}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Total cost</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-violet-600 inline-flex items-center">
                        <DollarSign size={16} />{(data.total_cost || 0).toFixed(2)}
                    </div>
                </div>
            </div>

            <div className="card-soft p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9" data-testid="ver-filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any status</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="h-9" data-testid="ver-filter-type"><SelectValue placeholder="Any type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any type</SelectItem>
                        {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : data.results.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <ShieldCheck size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No verifications yet.</div>
                    <div className="mt-1 text-xs text-muted-foreground">Click &quot;New verification&quot; to create one.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">Candidate</th>
                                <th className="p-3 font-semibold">Type</th>
                                <th className="p-3 font-semibold">Status</th>
                                <th className="p-3 font-semibold">PII preview</th>
                                <th className="p-3 font-semibold text-right">Cost</th>
                                <th className="p-3 font-semibold">Requested</th>
                                <th className="p-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.results.map((v) => {
                                const preview = v.pii_preview || {};
                                return (
                                    <tr key={v.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`ver-row-${v.id}`}>
                                        <td className="p-3">
                                            <div className="font-semibold text-foreground truncate max-w-[220px]">{v.candidate_name || v.candidate_email_masked}</div>
                                            <div className="text-[10px] text-muted-foreground truncate max-w-[220px]" data-testid={`ver-row-${v.id}-email`}>{v.candidate_email_masked}</div>
                                        </td>
                                        <td className="p-3 text-xs text-foreground capitalize">{v.type}</td>
                                        <td className="p-3">
                                            <Badge className={`text-[10px] border ${statusTone(v.status)}`}>{v.status}</Badge>
                                        </td>
                                        <td className="p-3 text-[10px] mono-score text-muted-foreground whitespace-nowrap">
                                            {preview.ssn_masked && <div>SSN {preview.ssn_masked}</div>}
                                            {preview.dob_masked && <div>DOB {preview.dob_masked}</div>}
                                            {preview.doc_number_masked && (
                                                <div>{preview.doc_type || "Doc"} {preview.doc_number_masked}</div>
                                            )}
                                            {!preview.ssn_masked && !preview.dob_masked && !preview.doc_number_masked && (
                                                <span className="italic text-muted-foreground/70">no PII</span>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-right mono-score">${(v.cost || 0).toFixed(2)}</td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                            {v.requested_at ? new Date(v.requested_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                {v.has_pii && (
                                                    <button
                                                        className="btn-outline text-[11px] px-2 py-1 inline-flex items-center gap-1"
                                                        onClick={() => openReveal(v)}
                                                        data-testid={`ver-reveal-${v.id}`}
                                                    >
                                                        <Eye size={11} /> Reveal
                                                    </button>
                                                )}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button className="btn-outline text-[11px] px-2 py-1" data-testid={`ver-actions-${v.id}`}>
                                                            Update
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {STATUSES.filter((s) => s !== v.status).map((s) => (
                                                            <DropdownMenuItem key={s} onClick={() => updateStatus(v, s)} data-testid={`ver-${v.id}-set-${s}`}>
                                                                Mark {s}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="ver-create-dialog" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-500" /> New verification
                        </DialogTitle>
                        <DialogDescription>Create a verification request. PII fields are encrypted at rest.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
                        <Input placeholder="Candidate email *" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="ver-form-email" />
                        <Input placeholder="Candidate name" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="ver-form-name" />
                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                            <SelectTrigger data-testid="ver-form-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Provider (e.g. jumio, manual)" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="ver-form-provider" />
                        <Input placeholder="Cost (USD)" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} type="number" step="0.01" data-testid="ver-form-cost" />

                        <div className="mt-2 rounded-xl border border-dashed border-border p-3">
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                                <Lock size={11} /> Encrypted PII (optional)
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Input placeholder="SSN" value={form.pii.ssn} onChange={(e) => setForm({ ...form, pii: { ...form.pii, ssn: e.target.value } })} data-testid="ver-form-ssn" />
                                <Input placeholder="Date of birth (YYYY-MM-DD)" value={form.pii.dob} onChange={(e) => setForm({ ...form, pii: { ...form.pii, dob: e.target.value } })} data-testid="ver-form-dob" />
                                <Input placeholder="Doc type (passport, license…)" value={form.pii.doc_type} onChange={(e) => setForm({ ...form, pii: { ...form.pii, doc_type: e.target.value } })} data-testid="ver-form-doctype" />
                                <Input placeholder="Doc number" value={form.pii.doc_number} onChange={(e) => setForm({ ...form, pii: { ...form.pii, doc_number: e.target.value } })} data-testid="ver-form-docnum" />
                            </div>
                            <Textarea placeholder="Sensitive notes (encrypted)" value={form.pii.sensitive_notes} rows={2} onChange={(e) => setForm({ ...form, pii: { ...form.pii, sensitive_notes: e.target.value } })} className="mt-2" data-testid="ver-form-notes" />
                            <div className="mt-2 text-[10px] text-muted-foreground">
                                These values are encrypted with AES/Fernet before storage and never appear in list views.
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setCreateOpen(false)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={create} disabled={submitting || !form.candidate_email.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="ver-form-submit">
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : "Create"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reveal dialog */}
            <Dialog open={!!revealTarget} onOpenChange={(o) => { if (!o) { setRevealTarget(null); setRevealed(null); setRevealReason(""); } }}>
                <DialogContent data-testid="ver-reveal-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye size={16} className="text-emerald-500" /> Reveal PII (audited)
                        </DialogTitle>
                        <DialogDescription>
                            This action is restricted to <b>platform_admin</b> and is written to the audit log along with the
                            justification you provide below.
                        </DialogDescription>
                    </DialogHeader>
                    {revealed ? (
                        <div className="space-y-2 py-1">
                            {Object.keys(revealed).length === 0 ? (
                                <div className="text-sm text-muted-foreground">No PII fields were stored on this record.</div>
                            ) : (
                                <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1" data-testid="ver-reveal-payload">
                                    {Object.entries(revealed).map(([k, v]) => (
                                        <div key={k} className="flex justify-between gap-3">
                                            <span className="text-muted-foreground text-xs uppercase tracking-wider">{k}</span>
                                            <span className="mono-score text-foreground break-all text-right">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="text-[11px] text-muted-foreground">
                                A <b>verification.pii_reveal</b> audit entry has been recorded for this access.
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2 py-2">
                            <div className="text-xs text-muted-foreground">
                                Verification: <b className="text-foreground">{revealTarget?.candidate_name || revealTarget?.candidate_email_masked}</b> · {revealTarget?.type}
                            </div>
                            <Textarea
                                placeholder="Business justification (min 8 chars) — e.g., 'Identity check challenge from provider X, case #123'"
                                value={revealReason}
                                onChange={(e) => setRevealReason(e.target.value)}
                                rows={3}
                                data-testid="ver-reveal-reason"
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <button
                            onClick={() => { setRevealTarget(null); setRevealed(null); setRevealReason(""); }}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="ver-reveal-close"
                        >
                            Close
                        </button>
                        {!revealed && (
                            <button
                                onClick={doReveal}
                                disabled={revealLoading || revealReason.trim().length < 8}
                                className="btn-green text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1"
                                data-testid="ver-reveal-confirm"
                            >
                                {revealLoading ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Reveal &amp; log
                            </button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
